import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ComplianceBadge } from './ComplianceBadge';
import { formatDateAU } from '../../../shared/format';
import type { ComplianceRow } from '../../../shared/types';
import type { ClientWithCompliance } from '../../../shared/api';

const FIELD_LABELS: Array<[keyof ComplianceRow, string]> = [
  ['riskProfile', 'Risk profile (5 years)'],
  ['factFind', 'Fact find (3 years)'],
  ['snapshot', 'Snapshot (6 months)'],
  ['fileNote', 'File note (90 days)'],
  ['bestInterest', 'Best interest (12 months)'],
  ['soaSigned', 'SoA signed (2 years)'],
  ['feeConsent', 'Fee consent (12 months)'],
  ['updateNeeded', 'Update needed'],
  ['writeSoa', 'Write SoA outstanding'],
  ['writeRoa', 'Write RoA outstanding'],
];

const FIELD_MAP = {
  riskProfile: 'lastRiskProfileDate',
  factFind: 'lastFactFindDate',
  snapshot: 'lastSnapshotDate',
  fileNote: 'lastFileNoteDate',
  bestInterest: 'lastBestInterestDate',
  soaSigned: 'soaSignedDate',
  feeConsent: 'lastFeeConsentDate',
} as const;

const DATE_FIELDS = [
  ['lastRiskProfileDate', 'Last risk profile'],
  ['lastFactFindDate', 'Last fact find'],
  ['lastSnapshotDate', 'Last snapshot'],
  ['lastBestInterestDate', 'Last best interest'],
  ['soaSignedDate', 'SoA signed date'],
  ['lastFeeConsentDate', 'Last fee consent'],
  ['qacExpiry', 'QAC expiry'],
  ['reviewDue', 'Review due'],
  ['callDue', 'Call due'],
] as const;

export function ComplianceTab({ client: c, canEdit }: { client: ClientWithCompliance; canEdit: boolean }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string | boolean>>({});
  const save = useMutation({
    mutationFn: () => api(`/api/clients/${c.id}/compliance`, { method: 'PATCH', body: JSON.stringify(draft) }),
    onSuccess: () => {
      setDraft({});
      qc.invalidateQueries({ queryKey: ['client', c.id] });
    },
  });

  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
      <section className="qa-card p-6">
        <p className="qa-eyebrow mb-4">Compliance state</p>
        <table className="qa-table">
          <tbody>
            {FIELD_LABELS.map(([k, label]) => (
              <tr key={k}>
                <td>{label}</td>
                <td><ComplianceBadge state={c.compliance[k]} label={label} /></td>
                <td className="text-xs text-ink-mute qa-mono">
                  {k in FIELD_MAP ? formatDateAU(c[FIELD_MAP[k as keyof typeof FIELD_MAP]]) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="qa-card p-6">
        <p className="qa-eyebrow mb-4">Update dates</p>
        {!canEdit && <p className="text-sm text-ink-mute mb-3">Read-only — request a client.edit role to change.</p>}
        <div className="space-y-3">
          {DATE_FIELDS.map(([key, label]) => (
            <div key={key} className="flex items-center gap-3">
              <label className="qa-label w-44 mb-0">{label}</label>
              <input type="date" disabled={!canEdit} className="qa-input flex-1"
                defaultValue={c[key] ?? ''}
                onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))} />
            </div>
          ))}
          <div className="flex flex-wrap gap-4 pt-2">
            <label className="flex items-center gap-2">
              <input type="checkbox" disabled={!canEdit} defaultChecked={c.soaNeeded ?? false}
                onChange={e => setDraft(d => ({ ...d, soaNeeded: e.target.checked }))} />
              <span className="text-sm">SoA outstanding</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" disabled={!canEdit} defaultChecked={c.roaNeeded ?? false}
                onChange={e => setDraft(d => ({ ...d, roaNeeded: e.target.checked }))} />
              <span className="text-sm">RoA outstanding</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" disabled={!canEdit} defaultChecked={c.updatePending ?? false}
                onChange={e => setDraft(d => ({ ...d, updatePending: e.target.checked }))} />
              <span className="text-sm">Update pending</span>
            </label>
          </div>
        </div>
        {canEdit && (
          <div className="flex justify-end mt-5">
            <button className="btn-primary" disabled={save.isPending || Object.keys(draft).length === 0} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save compliance updates'}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
